import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, Table, Tag, Typography, Row, Col, Statistic,
    Avatar, Button, Alert, Spin, Space, Dropdown, message,
    Modal, Segmented, Input, Divider
} from 'antd';
import {
    PercentageOutlined, ArrowLeftOutlined,
    TeamOutlined, CrownOutlined, UserOutlined,
    CalendarOutlined, DollarOutlined, WalletOutlined,
    DownloadOutlined, FileExcelOutlined, ReloadOutlined,
    CheckCircleOutlined, ClockCircleOutlined, EditOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import affiliatesService from '../../common/services/affiliates.service';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import XLSX from 'xlsx-js-style';

dayjs.locale('fr');

const { Title, Text, Paragraph } = Typography;

const RevenuesMonthlyRetentionsDetailPage = () => {
    const { type, beneficiaryId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [exporting, setExporting] = useState(false);

    // Payment modal states
    const [paymentModal, setPaymentModal] = useState({ visible: false, period: null });
    const [paymentMode, setPaymentMode] = useState('wave');
    const [paymentRef, setPaymentRef] = useState('');
    const [sendingWavePayout, setSendingWavePayout] = useState(false);
    const [savingPayment, setSavingPayment] = useState(false);

    const fetchDetail = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await affiliatesService.getMonthlyRetentionDetail(type, beneficiaryId);
            if (response.success) {
                setData(response.data);
            } else {
                setError(response.message || 'Erreur lors du chargement');
            }
        } catch (err) {
            console.error('Erreur:', err);
            setError('Erreur lors du chargement des détails');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetail();
    }, [type, beneficiaryId]);

    // Open payment modal for a period
    const handlePaymentClick = (periodData) => {
        setPaymentModal({ visible: true, period: periodData });
        setPaymentMode('wave');
        setPaymentRef('');
    };

    // Wave payout handler
    const handleWavePayout = async () => {
        const periodData = paymentModal.period;
        if (!periodData) return;

        const amountToPay = periodData.totalRetention || 0;

        Modal.confirm({
            title: 'Confirmer le paiement Wave',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>Vous allez envoyer <strong>{amountToPay.toLocaleString()} F</strong> via Wave à :</p>
                    <p><strong>{data?.beneficiary?.firstName} {data?.beneficiary?.lastName}</strong></p>
                    <p style={{ color: '#8c8c8c' }}>{data?.beneficiary?.phone}</p>
                    <p style={{ fontSize: 12, color: '#888' }}>Période : {periodData.periodLabel || periodData.period}</p>
                    <p style={{ marginTop: 12, color: '#faad14' }}>
                        Cette action est irréversible. L'argent sera débité de votre compte Wave Business.
                    </p>
                </div>
            ),
            okText: 'Confirmer et payer',
            cancelText: 'Annuler',
            okButtonProps: { style: { background: '#1890ff' } },
            onOk: async () => {
                setSendingWavePayout(true);
                try {
                    const response = await affiliatesService.sendMonthlyRetentionWavePayout({
                        beneficiaryId,
                        beneficiaryType: type,
                        period: periodData.period,
                        amount: amountToPay
                    });

                    if (response.success) {
                        message.success(`Paiement Wave effectué avec succès ! Référence: ${response.data.transactionId}`);
                        setPaymentModal({ visible: false, period: null });
                        setPaymentRef('');
                        fetchDetail();
                    } else {
                        message.error(response.message || 'Erreur lors du paiement Wave');
                    }
                } catch (error) {
                    console.error('Erreur Wave Payout:', error);
                    message.error(error.response?.data?.message || 'Erreur lors du paiement Wave');
                } finally {
                    setSendingWavePayout(false);
                }
            }
        });
    };

    // Manual payment handler
    const handleSavePayment = async () => {
        const periodData = paymentModal.period;
        if (!periodData) return;

        setSavingPayment(true);
        try {
            const response = await affiliatesService.payMonthlyRetention({
                beneficiaryId,
                beneficiaryType: type,
                period: periodData.period,
                paymentReference: paymentRef
            });

            if (response.success) {
                message.success('Paiement enregistré avec succès');
                setPaymentModal({ visible: false, period: null });
                setPaymentRef('');
                fetchDetail();
            } else {
                message.error(response.message || 'Erreur lors de la sauvegarde');
            }
        } catch (error) {
            console.error('Erreur sauvegarde paiement:', error);
            message.error('Erreur lors de la sauvegarde du paiement');
        } finally {
            setSavingPayment(false);
        }
    };

    // Payment status tag helper
    const getPaymentStatusTag = (status) => {
        if (status === 'paid') {
            return <Tag color="green" icon={<CheckCircleOutlined />}>Payé</Tag>;
        } else if (status === 'partial') {
            return <Tag color="blue" icon={<ClockCircleOutlined />}>Partiel</Tag>;
        }
        return <Tag color="orange" icon={<ClockCircleOutlined />}>En attente</Tag>;
    };

    // Fonction pour préparer les données d'export
    const prepareExportData = () => {
        if (!data?.periods) return [];

        const exportData = [];
        data.periods.forEach(periodData => {
            (periodData.drivers || []).forEach(driver => {
                const driverInfo = driver.driverId || {};
                exportData.push({
                    'Période': periodData.periodLabel || periodData.period,
                    'Chauffeur': `${driverInfo.firstName || ''} ${driverInfo.lastName || ''}`.trim(),
                    'Téléphone': driverInfo.phone || '',
                    'Total Recharges (F)': driver.totalRecharges || 0,
                    'Wave (F)': driver.waveRecharges || 0,
                    'Orange Money (F)': driver.orangeMoneyRecharges || 0,
                    'Commission (F)': driver.commission || 0
                });
            });
        });

        return exportData;
    };

    // Export CSV
    const exportToCSV = () => {
        setExporting(true);
        try {
            const exportData = prepareExportData();
            if (exportData.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            const benefName = `${data?.beneficiary?.firstName || ''} ${data?.beneficiary?.lastName || ''}`.trim();
            const benefType = type === 'ambassador' ? 'AMBASSADEUR' : 'AFFILIÉ';
            const titleLine = `RÉTENTIONS MENSUELLES DE ${benefName.toUpperCase()} (${benefType})`;
            const periodLine = `EXPORTÉ LE: ${dayjs().format('DD/MM/YYYY à HH:mm')}`;

            const headers = Object.keys(exportData[0]);
            const csvContent = [
                titleLine,
                periodLine,
                '',
                headers.join(';'),
                ...exportData.map(row => headers.map(h => `"${row[h]}"`).join(';'))
            ].join('\n');

            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            const benefNameFile = benefName.replace(/\s+/g, '_');
            const filename = `retentions_${benefNameFile}_${dayjs().format('YYYY-MM-DD_HHmm')}.csv`;

            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.success(`Export CSV réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export CSV:', error);
            message.error('Erreur lors de l\'export CSV');
        } finally {
            setExporting(false);
        }
    };

    // Export Excel
    const exportToExcel = () => {
        setExporting(true);
        try {
            const exportData = prepareExportData();
            if (exportData.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            const benefName = `${data?.beneficiary?.firstName || ''} ${data?.beneficiary?.lastName || ''}`.trim();
            const benefType = type === 'ambassador' ? 'AMBASSADEUR' : 'AFFILIÉ';

            const titleStyle = {
                font: { bold: true, sz: 14, color: { rgb: "000000" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const periodStyle = {
                font: { bold: true, sz: 11, color: { rgb: "555555" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const headerStyle = {
                font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
                fill: { fgColor: { rgb: "722ED1" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };
            const cellStyle = {
                border: {
                    top: { style: "thin", color: { rgb: "CCCCCC" } },
                    bottom: { style: "thin", color: { rgb: "CCCCCC" } },
                    left: { style: "thin", color: { rgb: "CCCCCC" } },
                    right: { style: "thin", color: { rgb: "CCCCCC" } }
                },
                alignment: { vertical: "center" }
            };

            const titleText = `RÉTENTIONS MENSUELLES DE ${benefName.toUpperCase()} (${benefType})`;
            const periodText = `EXPORTÉ LE: ${dayjs().format('DD/MM/YYYY à HH:mm')}`;

            const headerRows = [
                [{ v: titleText, s: titleStyle }],
                [{ v: periodText, s: periodStyle }],
                []
            ];

            const worksheet = XLSX.utils.aoa_to_sheet(headerRows);

            const headers = Object.keys(exportData[0]);
            const dataWithHeaders = [
                headers.map(h => ({ v: h.toUpperCase(), s: headerStyle })),
                ...exportData.map(row => headers.map(h => ({ v: row[h], s: cellStyle })))
            ];

            XLSX.utils.sheet_add_aoa(worksheet, dataWithHeaders, { origin: 'A4' });

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Rétentions');

            const colWidths = headers.map(key => ({
                wch: Math.max(key.length, ...exportData.map(row => String(row[key] || '').length), 15) + 2
            }));
            worksheet['!cols'] = colWidths;

            const colCount = headers.length;
            worksheet['!merges'] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
                { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } }
            ];

            worksheet['!rows'] = [
                { hpt: 25 },
                { hpt: 20 },
                { hpt: 15 },
                { hpt: 22 }
            ];

            const benefNameFile = benefName.replace(/\s+/g, '_');
            const filename = `retentions_${benefNameFile}_${dayjs().format('YYYY-MM-DD_HHmm')}.xlsx`;
            XLSX.writeFile(workbook, filename);

            message.success(`Export Excel réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export Excel:', error);
            message.error('Erreur lors de l\'export Excel');
        } finally {
            setExporting(false);
        }
    };

    const exportMenuItems = [
        {
            key: 'csv',
            label: 'Exporter en CSV',
            icon: <DownloadOutlined />,
            onClick: exportToCSV
        },
        {
            key: 'excel',
            label: 'Exporter en Excel',
            icon: <FileExcelOutlined />,
            onClick: exportToExcel
        }
    ];

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: 24 }}>
                <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/dashboard/revenues-monthly-retentions')}
                    style={{ marginBottom: 16 }}
                >
                    Retour
                </Button>
                <Alert type="error" message={error} showIcon />
            </div>
        );
    }

    const { beneficiary, periods, stats } = data;
    const isAmbassador = type === 'ambassador';

    return (
        <div className="monthly-retentions-detail-page">
            {/* Header */}
            <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/dashboard/revenues-monthly-retentions')}
                    >
                        Retour à la liste
                    </Button>
                    <Space>
                        <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                            <Button icon={<DownloadOutlined />} loading={exporting}>
                                Exporter
                            </Button>
                        </Dropdown>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchDetail}
                            loading={loading}
                        >
                            Actualiser
                        </Button>
                    </Space>
                </div>

                <Card>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                        <Avatar
                            size={70}
                            icon={isAmbassador ? <CrownOutlined /> : <TeamOutlined />}
                            style={{
                                backgroundColor: isAmbassador ? '#722ed1' : '#1890ff',
                                fontSize: 32
                            }}
                        />
                        <div style={{ flex: 1 }}>
                            <Title level={3} style={{ margin: 0 }}>
                                {beneficiary.firstName} {beneficiary.lastName}
                            </Title>
                            <Text type="secondary" style={{ fontSize: 16 }}>{beneficiary.phone}</Text>
                            <div style={{ marginTop: 8 }}>
                                <Tag
                                    color={isAmbassador ? 'purple' : 'blue'}
                                    icon={isAmbassador ? <CrownOutlined /> : <TeamOutlined />}
                                    style={{ fontSize: 14, padding: '4px 12px' }}
                                >
                                    {isAmbassador ? 'Ambassadeur' : 'Affilié'}
                                </Tag>
                            </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 14, color: '#888' }}>Total rétention</div>
                            <div style={{ fontSize: 28, fontWeight: 'bold', color: '#3f8600' }}>
                                {(stats.totalRetention || 0).toLocaleString()} F
                            </div>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Explication */}
            <Alert
                type="info"
                icon={<PercentageOutlined style={{ color: '#722ed1' }} />}
                message={
                    <span style={{ fontWeight: 600 }}>
                        Comment fonctionne la rétention mensuelle ?
                    </span>
                }
                description={
                    <Paragraph style={{ margin: 0, marginTop: 8 }}>
                        Chaque mois, une commission est calculée sur les recharges effectuées par les chauffeurs :
                        <ul style={{ marginTop: 8, marginBottom: 0 }}>
                            <li><strong style={{ color: '#1890ff' }}>Affilié :</strong> reçoit <strong>1%</strong> du total des recharges de ses chauffeurs</li>
                            <li><strong style={{ color: '#722ed1' }}>Ambassadeur :</strong> reçoit <strong>0.2%</strong> du total des recharges des chauffeurs de ses affiliés</li>
                        </ul>
                        {isAmbassador && (
                            <span style={{ display: 'block', marginTop: 8, color: '#722ed1' }}>
                                <strong>Note :</strong> En tant qu'ambassadeur qui est aussi affilié, vous cumulez les deux commissions pour vos chauffeurs directs.
                            </span>
                        )}
                    </Paragraph>
                }
                style={{ marginBottom: 24 }}
            />

            {/* Statistiques */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title={<span><PercentageOutlined style={{ marginRight: 8 }} />Total rétentions</span>}
                            value={(stats.totalRetention || 0).toLocaleString()}
                            suffix="F"
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title={<span><CheckCircleOutlined style={{ marginRight: 8 }} />Payé</span>}
                            value={(stats.totalPaid || 0).toLocaleString()}
                            suffix="F"
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title={<span><ClockCircleOutlined style={{ marginRight: 8 }} />En attente</span>}
                            value={(stats.totalPending || 0).toLocaleString()}
                            suffix="F"
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title={<span><UserOutlined style={{ marginRight: 8 }} />Chauffeurs</span>}
                            value={stats.driversCount}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Historique par période */}
            <Card
                title={
                    <span>
                        <CalendarOutlined style={{ marginRight: 8 }} />
                        Historique par période
                    </span>
                }
            >
                {periods.length === 0 ? (
                    <Alert type="info" message="Aucune rétention enregistrée" showIcon />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {periods.map((periodData, index) => (
                            <Card
                                key={periodData.period}
                                size="small"
                                style={{
                                    backgroundColor: '#fafafa',
                                    borderLeft: '3px solid #722ed1'
                                }}
                                title={
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <CalendarOutlined style={{ color: index === 0 ? '#52c41a' : '#1890ff' }} />
                                            <span style={{ fontWeight: 600 }}>
                                                {periodData.periodLabel || periodData.period}
                                            </span>
                                            <Tag color="purple">
                                                <PercentageOutlined style={{ marginRight: 4 }} />
                                                {(periodData.totalRetention || 0).toLocaleString()} F
                                            </Tag>
                                            {getPaymentStatusTag(periodData.paymentStatus)}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <span style={{ fontSize: 13, color: '#888' }}>
                                                <WalletOutlined style={{ marginRight: 4 }} />
                                                Recharges: <strong>{(periodData.totalRecharges || 0).toLocaleString()} F</strong>
                                                {' '} | {periodData.driversCount} chauffeur(s)
                                            </span>
                                            {periodData.paymentStatus !== 'paid' && (
                                                <Button
                                                    type="primary"
                                                    size="small"
                                                    icon={<DollarOutlined />}
                                                    onClick={(e) => { e.stopPropagation(); handlePaymentClick(periodData); }}
                                                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                                >
                                                    Payer
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                }
                            >
                                <Table
                                    dataSource={periodData.drivers}
                                    rowKey={(record) => record._id}
                                    pagination={{ pageSize: 10, hideOnSinglePage: true, size: 'small' }}
                                    size="small"
                                    scroll={{ x: 650 }}
                                    columns={[
                                        {
                                            title: 'Chauffeur',
                                            key: 'driver',
                                            width: 180,
                                            ellipsis: true,
                                            render: (_, record) => {
                                                const driver = record.driverId || {};
                                                return (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <Avatar
                                                            size={28}
                                                            icon={<UserOutlined />}
                                                            style={{ backgroundColor: '#52c41a', flexShrink: 0 }}
                                                        />
                                                        <div style={{ minWidth: 0 }}>
                                                            <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                {driver.firstName} {driver.lastName}
                                                            </div>
                                                            <div style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>
                                                                {driver.phone}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Recharges',
                                            key: 'totalRecharges',
                                            width: 120,
                                            render: (_, record) => (
                                                <span style={{ color: '#1890ff', fontWeight: 600 }}>
                                                    {(record.totalRecharges || 0).toLocaleString()} F
                                                </span>
                                            )
                                        },
                                        {
                                            title: 'Wave',
                                            key: 'wave',
                                            width: 100,
                                            render: (_, record) => (
                                                <span style={{ color: '#1890ff', fontSize: 13 }}>
                                                    {(record.waveRecharges || 0).toLocaleString()} F
                                                </span>
                                            )
                                        },
                                        {
                                            title: 'OM',
                                            key: 'orangeMoney',
                                            width: 100,
                                            render: (_, record) => (
                                                <span style={{ color: '#fa8c16', fontSize: 13 }}>
                                                    {(record.orangeMoneyRecharges || 0).toLocaleString()} F
                                                </span>
                                            )
                                        },
                                        {
                                            title: 'Commission',
                                            key: 'commission',
                                            width: 110,
                                            fixed: 'right',
                                            render: (_, record) => (
                                                <span style={{ fontWeight: 'bold', color: '#3f8600' }}>
                                                    {(record.commission || 0).toLocaleString()} F
                                                </span>
                                            )
                                        }
                                    ]}
                                />
                            </Card>
                        ))}
                    </div>
                )}
            </Card>

            {/* Résumé */}
            <Card
                title={
                    <span>
                        <DollarOutlined style={{ marginRight: 8 }} />
                        Résumé des rétentions
                    </span>
                }
                style={{ marginTop: 24 }}
            >
                <Row gutter={[24, 16]}>
                    <Col xs={24} sm={8}>
                        <div style={{ textAlign: 'center', padding: 16, backgroundColor: '#f9f0ff', borderRadius: 8 }}>
                            <PercentageOutlined style={{ fontSize: 32, color: '#722ed1', marginBottom: 8 }} />
                            <div style={{ fontSize: 12, color: '#888' }}>Total rétentions</div>
                            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#722ed1' }}>
                                {(stats.totalRetention || 0).toLocaleString()} F
                            </div>
                        </div>
                    </Col>
                    <Col xs={24} sm={8}>
                        <div style={{ textAlign: 'center', padding: 16, backgroundColor: '#f0f5ff', borderRadius: 8 }}>
                            <WalletOutlined style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }} />
                            <div style={{ fontSize: 12, color: '#888' }}>Total recharges</div>
                            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                                {(stats.totalRecharges || 0).toLocaleString()} F
                            </div>
                        </div>
                    </Col>
                    <Col xs={24} sm={8}>
                        <div style={{ textAlign: 'center', padding: 16, backgroundColor: '#f6ffed', borderRadius: 8 }}>
                            <UserOutlined style={{ fontSize: 32, color: '#52c41a', marginBottom: 8 }} />
                            <div style={{ fontSize: 12, color: '#888' }}>Chauffeurs</div>
                            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>
                                {stats.driversCount || 0}
                            </div>
                        </div>
                    </Col>
                </Row>
            </Card>

            {/* Modal de paiement */}
            <Modal
                title={
                    <span>
                        <DollarOutlined style={{ marginRight: 8 }} />
                        Enregistrer un paiement
                    </span>
                }
                open={paymentModal.visible}
                onCancel={() => {
                    setPaymentModal({ visible: false, period: null });
                    setPaymentRef('');
                }}
                footer={paymentMode === 'manual' ? [
                    <Button key="cancel" onClick={() => {
                        setPaymentModal({ visible: false, period: null });
                        setPaymentRef('');
                    }}>
                        Annuler
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        loading={savingPayment}
                        onClick={handleSavePayment}
                        style={{ background: '#52c41a', borderColor: '#52c41a' }}
                    >
                        Enregistrer le paiement
                    </Button>
                ] : null}
                width={500}
            >
                {paymentModal.period && (
                    <>
                        {/* Infos de la période */}
                        <div style={{
                            background: '#fafafa',
                            borderRadius: 8,
                            padding: 16,
                            marginBottom: 16
                        }}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <div style={{ fontSize: 12, color: '#888' }}>Période</div>
                                    <div style={{ fontWeight: 600 }}>
                                        {paymentModal.period.periodLabel || paymentModal.period.period}
                                    </div>
                                </Col>
                                <Col span={8}>
                                    <div style={{ fontSize: 12, color: '#888' }}>Montant rétention</div>
                                    <div style={{ fontWeight: 600, color: '#722ed1' }}>
                                        {(paymentModal.period.totalRetention || 0).toLocaleString()} F
                                    </div>
                                </Col>
                                <Col span={8}>
                                    <div style={{ fontSize: 12, color: '#888' }}>Statut</div>
                                    {getPaymentStatusTag(paymentModal.period.paymentStatus)}
                                </Col>
                            </Row>
                            <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                                {paymentModal.period.driversCount} chauffeur(s) | Recharges: {(paymentModal.period.totalRecharges || 0).toLocaleString()} F
                            </div>
                        </div>

                        <Divider style={{ margin: '12px 0' }} />

                        {/* Sélecteur de mode de paiement */}
                        <div style={{ marginBottom: 20 }}>
                            <Segmented
                                value={paymentMode}
                                onChange={(value) => {
                                    setPaymentMode(value);
                                    setPaymentRef('');
                                }}
                                block
                                size="large"
                                options={[
                                    {
                                        value: 'wave',
                                        label: (
                                            <div style={{ padding: '4px 0' }}>
                                                <WalletOutlined style={{ marginRight: 6 }} />
                                                Payer via Wave
                                            </div>
                                        )
                                    },
                                    {
                                        value: 'manual',
                                        label: (
                                            <div style={{ padding: '4px 0' }}>
                                                <EditOutlined style={{ marginRight: 6 }} />
                                                Saisie manuelle
                                            </div>
                                        )
                                    }
                                ]}
                            />
                        </div>

                        {/* MODE WAVE */}
                        {paymentMode === 'wave' && (
                            <div style={{
                                background: '#f0f5ff',
                                border: '1px solid #adc6ff',
                                borderRadius: 10,
                                padding: 20
                            }}>
                                <div style={{ marginBottom: 16, textAlign: 'center' }}>
                                    <div style={{ fontSize: 13, color: '#595959' }}>
                                        Le paiement de <strong>{(paymentModal.period.totalRetention || 0).toLocaleString()} F</strong> sera envoyé à :
                                    </div>
                                    <div style={{ fontWeight: 600, fontSize: 16, marginTop: 4 }}>
                                        {beneficiary?.firstName} {beneficiary?.lastName}
                                    </div>
                                    <div style={{ color: '#8c8c8c' }}>{beneficiary?.phone}</div>
                                </div>

                                <Button
                                    type="primary"
                                    size="large"
                                    block
                                    icon={<WalletOutlined />}
                                    loading={sendingWavePayout}
                                    onClick={handleWavePayout}
                                    style={{
                                        background: 'linear-gradient(135deg, #1a73e8 0%, #4285f4 100%)',
                                        border: 'none',
                                        height: 48,
                                        fontWeight: 600,
                                        borderRadius: 8
                                    }}
                                >
                                    Envoyer {(paymentModal.period.totalRetention || 0).toLocaleString()} F via Wave
                                </Button>
                                <div style={{ marginTop: 8, fontSize: 11, color: '#5b8c00', textAlign: 'center' }}>
                                    Le paiement sera envoyé directement sur Wave et la référence sera automatiquement enregistrée
                                </div>
                            </div>
                        )}

                        {/* MODE MANUEL */}
                        {paymentMode === 'manual' && (
                            <div style={{
                                background: '#fffbe6',
                                border: '1px solid #ffe58f',
                                borderRadius: 10,
                                padding: 20
                            }}>
                                <div style={{ marginBottom: 16 }}>
                                    <div style={{ marginBottom: 6, fontWeight: 500 }}>Référence de paiement</div>
                                    <Input
                                        placeholder="Ex: TX-2025021001234"
                                        value={paymentRef}
                                        onChange={(e) => setPaymentRef(e.target.value)}
                                        size="large"
                                        prefix={<WalletOutlined style={{ color: '#bfbfbf' }} />}
                                    />
                                    <div style={{ marginTop: 6, fontSize: 11, color: '#8c8c8c' }}>
                                        Entrez la référence si vous avez déjà effectué le paiement
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center', fontSize: 13, color: '#595959' }}>
                                    Montant : <strong style={{ color: '#722ed1' }}>{(paymentModal.period.totalRetention || 0).toLocaleString()} F</strong>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Modal>
        </div>
    );
};

export default RevenuesMonthlyRetentionsDetailPage;
